import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { experimentsApi } from '@/lib/api/client'

export function isBackofficeStudyPreview(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return window.location.pathname.includes('/explore/studies/')
}

function cloneJson<T>(value: T): T {
  return value == null ? value : JSON.parse(JSON.stringify(value))
}

function serialize(value: unknown): string {
  return JSON.stringify(value ?? {})
}

export function useBackofficeSummaryEdits<T extends Record<string, unknown>>(
  studyId: string,
  executiveSummary: T | undefined,
) {
  const canEdit = isBackofficeStudyPreview()
  const incoming = serialize(executiveSummary)
  const [baseline, setBaseline] = useState(incoming)
  const [draft, setDraft] = useState<T>(() => cloneJson(executiveSummary ?? ({} as T)))
  const [hasLocalEdits, setHasLocalEdits] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (hasLocalEdits) {
      return
    }
    setBaseline(incoming)
    setDraft(JSON.parse(incoming) as T)
  }, [hasLocalEdits, incoming])

  const isDirty = hasLocalEdits && serialize(draft) !== baseline

  const applyPatch = useCallback((patch: Partial<T>) => {
    if (!canEdit) {
      return
    }
    setHasLocalEdits(true)
    setDraft((current) => ({
      ...(cloneJson(current) ?? {}),
      ...patch,
    }) as T)
  }, [canEdit])

  const save = useCallback(async () => {
    if (!canEdit || serialize(draft) === baseline) {
      return
    }
    setIsSaving(true)
    try {
      await experimentsApi.patchAdmin(studyId, {
        op: 'update_artifact',
        artifactType: 'summary',
        value: draft,
      })
      if (typeof draft.headline === 'string') {
        await experimentsApi.patchAdmin(studyId, {
          op: 'update_field',
          field: 'reportHeadline',
          value: draft.headline,
        })
      }
      setBaseline(serialize(draft))
      setHasLocalEdits(false)
      toast.success('Saved — live for the client')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Save failed')
    } finally {
      setIsSaving(false)
    }
  }, [baseline, canEdit, draft, studyId])

  return useMemo(
    () => ({
      canEdit,
      summary: draft,
      applyPatch,
      save,
      isDirty,
      isSaving,
    }),
    [applyPatch, canEdit, draft, isDirty, isSaving, save],
  )
}
